import { Link } from "@tanstack/react-router";
import { Section, SectionHeading, Eyebrow, CheckList, FaqAccordion, CtaBand, TrustBar } from "@/components/sections";
import { QuoteForm } from "@/components/QuoteForm";
import { SECONDARY_SERVICES, SERVICE_AREAS, type FaqItem } from "@/lib/site";
import { baseComparisonRows, basePriceFactors, whyMatters, type ComparisonRow, type PriceFactor, type PricingTier, type ValueProp } from "@/lib/service-content";
import { Boxes, Truck, Clock, ShieldCheck, PhoneCall, CalendarCheck, Recycle, Sparkles, BadgeDollarSign, ClipboardCheck, Home } from "lucide-react";
import { inspectorImg } from "@/lib/inspector-image";

export type ServicePageProps = {
  slug: string;
  title: string;
  h1: string;
  intro: string;
  heroImg: string;
  benefits: string[];
  scenarios: string[];
  whyUs: string[];
  localContent: string;
  faqs: FaqItem[];
  serviceForForm: string;
  pricingTiers?: PricingTier[];
  pricingNote?: string;
  priceFactors?: PriceFactor[];
  valueProps?: ValueProp[];
  valueNote?: string;
  takeList?: string[];
  cannotTakeList?: string[];
  comparisonRows?: ComparisonRow[];
  beforeAfter?: { before: string; after: string; caption: string }[];
};

const factorIcons = [Boxes, Truck, Clock];

const includeItems = [
  { icon: ClipboardCheck, t: "Free On-Site Assessment", d: "We look at the job in person or by photo and quote it before any work starts." },
  { icon: BadgeDollarSign, t: "All-Inclusive Flat Pricing", d: "Labor, loading, fuel, disposal fees and clean-up are all inside one written number." },
  { icon: Truck, t: "Full Loading & Hauling", d: "You point, we lift. Our crew carries everything out — no dragging items to the curb." },
  { icon: Recycle, t: "Donation & Recycling First", d: "Usable items go to local Sarasota charities and recyclers before anything sees a landfill." },
  { icon: Sparkles, t: "Broom-Clean Finish", d: "We sweep the space when the truck is loaded so the area is ready to use again." },
  { icon: ShieldCheck, t: "Licensed, Insured & Bonded", d: "Background-checked, uniformed crews and full coverage on every Sarasota-area job." },
];

const processSteps = [
  { i: PhoneCall, t: "1. Reach Out", d: "Call or send the form with a few photos or a quick description of the job." },
  { i: CalendarCheck, t: "2. Schedule & Quote", d: "We confirm a window that works for you and give a firm written price on arrival." },
  { i: Truck, t: "3. We Do the Work", d: "Our crew loads, hauls and protects your floors, walls and landscaping as we go." },
  { i: Home, t: "4. Clean & Confirm", d: "We sweep up, walk the space with you and send photo confirmation when requested." },
];

export function ServicePage(p: ServicePageProps) {
  const related = SECONDARY_SERVICES.filter((s) => s.slug !== p.slug).slice(0, 4);
  const priceFactors = p.priceFactors ?? basePriceFactors;
  const comparisonRows = p.comparisonRows ?? baseComparisonRows;
  const wm = whyMatters[p.slug];
  return (
    <>
      <section className="relative isolate overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 -z-10">
          <img src={p.heroImg} alt="" fetchPriority="high" decoding="async" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.22_0.04_250)]/85 to-[oklch(0.22_0.04_250)]/60" />
        </div>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <div className="max-w-2xl">
            <Eyebrow><span className="text-[oklch(0.85_0.12_82)]">Sarasota · Suncoast</span></Eyebrow>
            <h1 className="font-serif text-4xl leading-tight sm:text-5xl">{p.h1}</h1>
            <p className="mt-5 text-base text-primary-foreground/85 sm:text-lg">{p.intro}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact" className="rounded-md bg-[oklch(0.78_0.12_82)] px-5 py-3 text-sm font-semibold text-[oklch(0.22_0.04_250)] hover:brightness-105">
                Get a Free Quote
              </Link>
              <a href="tel:+19415002320" className="rounded-md border border-primary-foreground/30 px-5 py-3 text-sm font-medium hover:bg-primary-foreground/10">
                Call (941) 500-2320
              </a>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      <Section>
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>Why it matters</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl">{wm?.title ?? "Clutter never clears itself."}</h2>
            <p className="mt-4 text-muted-foreground">
              {wm?.body ?? `Between Florida heat, humidity and the sheer weight of what needs to move, ${p.title.toLowerCase()} is rarely a one-afternoon job. Anchor Services brings the crew, the truck and the disposal channels — so the space is usable again today instead of next season.`}
            </p>
            <ul className="mt-6 space-y-3">
              {p.benefits.slice(0, 4).map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-foreground/85">
                  <span className="mt-1 inline-block h-1.5 w-1.5 flex-none rounded-full bg-[oklch(0.78_0.12_82)]" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={inspectorImg.src} srcSet={inspectorImg.srcSet} sizes={inspectorImg.sizes} alt="Anchor Services team member reviewing a job at a Sarasota property" width={inspectorImg.width} height={inspectorImg.height} loading="lazy" decoding="async" className="aspect-[16/9] w-full rounded-2xl object-cover shadow-lg" />
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-border bg-card p-4 shadow-md sm:block">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{wm?.statLabel ?? "Typical turnaround"}</div>
              <div className="font-serif text-2xl text-primary">{wm?.stat ?? "Same or next day"}</div>
            </div>
          </div>
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading
          eyebrow={p.title}
          title={`What's included with every ${p.title.toLowerCase()} job.`}
          intro="One crew, one price, one visit — here's what you get on every Sarasota-area job we take."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {includeItems.map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-xl border border-border bg-card p-6">
              <Icon className="h-6 w-6 text-[oklch(0.55_0.12_82)]" />
              <div className="mt-4 font-serif text-lg text-foreground">{t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Our process" title="How an Anchor Services job works" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {processSteps.map(({ i: Icon, t, d }) => (
            <div key={t} className="rounded-xl border border-border bg-card p-6">
              <Icon className="h-6 w-6 text-[oklch(0.55_0.12_82)]" />
              <div className="mt-4 font-serif text-lg text-primary">{t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {p.beforeAfter && p.beforeAfter.length > 0 && (
        <Section>
          <SectionHeading
            eyebrow="Before & after"
            title={`Real ${p.title.toLowerCase()} results`}
            intro="Same space, same day. Here's what a full Anchor Services cleanout looks like from start to finish."
          />
          <div className="mt-12 space-y-12">
            {p.beforeAfter.map((ba) => (
              <div key={ba.caption}>
                <div className="grid gap-6 sm:grid-cols-2">
                  {[
                    { label: "Before", src: ba.before, alt: `Sarasota property before Anchor Services ${p.title.toLowerCase()} — ${ba.caption}` },
                    { label: "After", src: ba.after, alt: `Clean Sarasota property after Anchor Services ${p.title.toLowerCase()} — ${ba.caption}` },
                  ].map((img) => (
                    <figure key={img.label} className="overflow-hidden rounded-xl border border-border bg-card">
                      <div className="relative">
                        <img src={img.src} alt={img.alt} width={1536} height={1024} loading="lazy" className="aspect-[3/2] w-full object-cover" />
                        <figcaption className="absolute left-4 top-4 rounded-md bg-[oklch(0.22_0.04_250)]/85 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
                          {img.label}
                        </figcaption>
                      </div>
                    </figure>
                  ))}
                </div>
                <p className="mt-4 text-center text-sm text-muted-foreground">{ba.caption}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {p.pricingTiers && (
        <Section tone="muted">
          <SectionHeading
            eyebrow="Plans & Pricing"
            title="Straightforward pricing by load size."
            intro="Transparent, upfront pricing for Sarasota homeowners — you see the price before we lift a single item."
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {p.pricingTiers.map((t) => (
              <div key={t.name} className="rounded-xl border border-border bg-card p-7">
                <div className="text-xs font-semibold uppercase tracking-wider text-[oklch(0.55_0.12_82)]">{t.badge}</div>
                <div className="mt-2 font-serif text-2xl text-primary">{t.name}</div>
                <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">{t.volume}</div>
                <div className="mt-3 font-semibold text-foreground">{t.range}</div>
                <p className="mt-3 text-sm text-muted-foreground">{t.desc}</p>
              </div>
            ))}
          </div>
          {p.pricingNote && (
            <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-muted-foreground">
              {p.pricingNote}{" "}
              <Link to="/contact" className="text-primary underline underline-offset-4">Request a custom quote</Link>.
            </p>
          )}
        </Section>
      )}

      <Section>
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>What affects your price</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl">Three variables that change your cost</h2>
            <p className="mt-4 text-muted-foreground">
              No two jobs are alike. Here's how we build a fair, custom quote for every Sarasota property we serve.
            </p>
            <div className="mt-8 space-y-6">
              {priceFactors.map((f, i) => {
                const Icon = factorIcons[i % factorIcons.length];
                return (
                  <div key={f.title} className="flex gap-4">
                    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[oklch(0.78_0.12_82)]/20 text-[oklch(0.55_0.12_82)]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{f.title}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-xl text-primary">Flat-rate, quoted upfront</div>
              <p className="mt-3 text-sm text-muted-foreground">
                We look at the job, give you a firm written price, and that's what you pay. Labor, loading, fuel, disposal and clean-up are all inside the number — no surprise line items when we're done.
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-xl text-primary">No contracts, no obligation</div>
              <p className="mt-3 text-sm text-muted-foreground">
                Estimates are free and there's nothing to sign. If the price isn't right for you, we shake hands and go — and you can call us back whenever you're ready.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {p.valueProps && (
        <Section tone="muted">
          <SectionHeading
            eyebrow="Return on investment"
            title="Why full-service beats doing it yourself"
            intro="Once you add up rentals, fees, fuel and a lost weekend, hiring a crew usually costs about the same — and takes a couple of hours instead."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {p.valueProps.map((v) => (
              <div key={v.title} className="rounded-xl border border-border bg-card p-6">
                <div className="font-serif text-lg text-primary">{v.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{v.desc}</p>
                <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-secondary/40 p-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Do it yourself</div>
                    <div className="mt-0.5 text-sm font-semibold text-foreground">{v.diy}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[oklch(0.55_0.12_82)]">With Anchor</div>
                    <div className="mt-0.5 text-sm font-semibold text-primary">{v.ours}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {p.valueNote && (
            <div className="mx-auto mt-10 max-w-3xl rounded-xl border border-[oklch(0.78_0.12_82)]/30 bg-[oklch(0.78_0.12_82)]/10 p-6 text-center">
              <p className="text-sm font-medium text-foreground">{p.valueNote}</p>
            </div>
          )}
        </Section>
      )}

      <Section>
        <SectionHeading
          eyebrow="Competitive comparison"
          title="Why homeowners choose Anchor Services"
          intro="We're not the cheapest option — and we don't try to be. Here's how we stack up against the competition."
        />
        <div className="mx-auto mt-10 max-w-3xl overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr>
                <th className="px-5 py-3 text-left font-medium text-foreground">Feature</th>
                <th className="px-5 py-3 text-center font-medium text-primary">Anchor Services</th>
                <th className="px-5 py-3 text-center font-medium text-muted-foreground">Typical competitor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {comparisonRows.map((row) => (
                <tr key={row.f}>
                  <td className="px-5 py-3 text-foreground/90">{row.f}</td>
                  <td className="px-5 py-3 text-center font-medium text-primary">{row.a}</td>
                  <td className="px-5 py-3 text-center text-muted-foreground">{row.c}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section tone="muted">
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2 lg:pr-6">
            <Eyebrow>Why homeowners choose this</Eyebrow>
            <h2 className="font-serif text-3xl">Benefits of {p.title.toLowerCase()}</h2>
            <div className="mt-6"><CheckList items={p.benefits} /></div>

            <h2 className="mt-14 font-serif text-3xl">Common scenarios</h2>
            <ul className="mt-6 space-y-3 text-sm text-foreground/85">
              {p.scenarios.map((s) => <li key={s} className="rounded-lg border border-border bg-card px-4 py-3">{s}</li>)}
            </ul>

            <h2 className="mt-14 font-serif text-3xl">Why Anchor Services</h2>
            <div className="mt-6"><CheckList items={p.whyUs} /></div>

            <h2 className="mt-14 font-serif text-3xl">Serving the Sarasota coast</h2>
            <p className="mt-4 text-muted-foreground">{p.localContent}</p>

            <div className="mt-12 rounded-xl border border-border bg-secondary/40 p-6">
              <div className="font-serif text-xl text-primary">Need more than {p.title.toLowerCase()}?</div>
              <p className="mt-2 text-sm text-muted-foreground">
                We're best known for our Sarasota{' '}
                <Link to="/home-watch-services" className="text-primary underline">Home Watch service</Link> — comprehensive monitoring for seasonal residents and absentee owners.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {related.map((r) => (
                  <Link key={r.slug} to={`/${r.slug}` as string} className="rounded-md border border-border bg-card px-3 py-1.5 text-xs text-foreground/80 hover:border-[oklch(0.78_0.12_82)]">
                    {r.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:sticky lg:top-24 lg:self-start">
            <QuoteForm sourcePage={`/${p.slug}`} defaultService={p.serviceForForm} />
          </div>
        </div>
      </Section>

      {(p.takeList || p.cannotTakeList) && (
        <Section>
          <SectionHeading eyebrow="Scope of work" title="What we take — and what we can't" />
          <div className="mx-auto mt-10 grid max-w-4xl gap-8 lg:grid-cols-2">
            {p.takeList && (
              <div className="rounded-xl border border-border bg-card p-6">
                <div className="font-serif text-xl text-primary">What we haul away</div>
                <div className="mt-5"><CheckList items={p.takeList} /></div>
              </div>
            )}
            {p.cannotTakeList && (
              <div className="rounded-xl border border-border bg-card p-6">
                <div className="font-serif text-xl text-primary">What we can't accept</div>
                <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
                  {p.cannotTakeList.map((i) => (
                    <li key={i} className="rounded-lg bg-secondary/40 px-4 py-2.5">{i}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Section>
      )}

      <Section tone="muted">
        <SectionHeading eyebrow="Service area" title="Serving the entire Sarasota coast" />
        <div className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-2">
          {SERVICE_AREAS.map((a) => (
            <Link key={a} to="/service-areas" className="rounded-full border border-border bg-card px-4 py-1.5 text-sm text-foreground/80 hover:border-[oklch(0.78_0.12_82)]">
              {a}
            </Link>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading title="Frequently asked questions" />
        <div className="mt-10"><FaqAccordion items={p.faqs} /></div>
      </Section>

      <CtaBand title={`Ready for ${p.title.toLowerCase()} in Sarasota?`} sub="Get a free, no-obligation quote today." />
    </>
  );
}